'use client';

import * as React from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import {
  getEffectiveVideoQualityProfile,
  getVideoCaptureMediaConstraints,
  readVideoQualityProfile,
} from '@/lib/video-quality-profile';
import { readEconomyModeEnabled } from '@/lib/economyMode';
import {
  getEffectiveScreenShareQualityProfile,
  getScreenShareMediaConstraints,
  readScreenShareQualityProfile,
} from '@/lib/screen-share-profile';
import {
  capLocalCameraForLargeRoom,
  restoreLocalCameraPublishBitrate,
} from '@/lib/camera-publish-bitrate';

/**
 * Plano estabilidade jul/2026: reduzido de 20 → 10 para capturar salas médias
 * com gravação ativa (egress = subscriber extra, aumenta carga SFU).
 *
 * Em escritório com ~20 PCs na mesma rede, cada um publicando ~850 kbps satura
 * o uplink compartilhado (~17 Mbps). Cap de encode + camadas LOW mitiga antes do Poor.
 */
export const LARGE_ROOM_PARTICIPANT_THRESHOLD = 10;

function isLargeRoom(numParticipants: number): boolean {
  return numParticipants > LARGE_ROOM_PARTICIPANT_THRESHOLD;
}

function getLocalMediaStreamTrack(
  room: Room,
  source: Track.Source,
): MediaStreamTrack | undefined {
  const pub = room.localParticipant.getTrackPublication(source);
  const t = pub?.track as { mediaStreamTrack?: MediaStreamTrack } | undefined;
  const ms = t?.mediaStreamTrack;
  return ms && ms.readyState === 'live' ? ms : undefined;
}

async function tightenLocalCameraForLargeRoom(room: Room) {
  // Só FPS + teto de publish — NÃO ideal 360p. Encolher captura mata a camada HIGH
  // do simulcast e todos passam a ver este participante em baixa resolução.
  const msTrack = getLocalMediaStreamTrack(room, Track.Source.Camera);
  if (msTrack) {
    try {
      await msTrack.applyConstraints({
        width: { max: 960 },
        height: { max: 540 },
        frameRate: { max: 20 },
      });
    } catch {
      // applyConstraints pode falhar em alguns browsers/dispositivos.
    }
  }
  await capLocalCameraForLargeRoom(room);
}

async function tightenLocalScreenShareForLargeRoom(room: Room) {
  const msTrack = getLocalMediaStreamTrack(room, Track.Source.ScreenShare);
  if (!msTrack) return;
  try {
    const profile = getEffectiveScreenShareQualityProfile(
      readScreenShareQualityProfile(),
      readEconomyModeEnabled(),
    );
    await msTrack.applyConstraints(getScreenShareMediaConstraints(profile, 'large-room'));
  } catch {
    // applyConstraints pode falhar em alguns browsers/dispositivos.
  }
}

async function restoreLocalCameraAfterLargeRoom(room: Room) {
  const msTrack = getLocalMediaStreamTrack(room, Track.Source.Camera);
  if (!msTrack) return;
  try {
    const economy = readEconomyModeEnabled();
    const effective = getEffectiveVideoQualityProfile(readVideoQualityProfile(), economy);
    await msTrack.applyConstraints(getVideoCaptureMediaConstraints(effective));
  } catch {
    // applyConstraints pode falhar em alguns browsers/dispositivos.
  }
  await restoreLocalCameraPublishBitrate(room);
}

async function restoreLocalScreenShareAfterLargeRoom(room: Room) {
  const msTrack = getLocalMediaStreamTrack(room, Track.Source.ScreenShare);
  if (!msTrack) return;
  try {
    const economy = readEconomyModeEnabled();
    const profile = getEffectiveScreenShareQualityProfile(
      readScreenShareQualityProfile(),
      economy,
    );
    await msTrack.applyConstraints(
      getScreenShareMediaConstraints(profile, economy ? 'economy' : 'default'),
    );
  } catch {
    // applyConstraints pode falhar em alguns browsers/dispositivos.
  }
}

export function useLargeRoomAdaptation(room: Room | undefined) {
  const largeRoomActiveRef = React.useRef(false);

  React.useEffect(() => {
    if (!room) return;

    const applyAdaptation = () => {
      const n = room.numParticipants;
      const large = isLargeRoom(n);

      if (!large) {
        if (largeRoomActiveRef.current) {
          largeRoomActiveRef.current = false;
          void restoreLocalCameraAfterLargeRoom(room);
          void restoreLocalScreenShareAfterLargeRoom(room);
        }
        return;
      }

      // NÃO forçar setVideoQuality nos remotos: isso só degrada a visão LOCAL e
      // parece que “meu PC baixou a qualidade da sala”. adaptiveStream decide.
      // Em sala grande só aliviamos o que ESTE cliente publica (upload próprio).
      //
      // NÃO usar prioritizePerformance(): ele desativa 2 das 3 camadas de simulcast
      // e o SFU marca este publisher como Poor (vermelho) enquanto a sala for grande.
      // O cap de bitrate/fps em tightenLocalCameraForLargeRoom mantém as camadas.

      if (!largeRoomActiveRef.current) {
        largeRoomActiveRef.current = true;
        void tightenLocalCameraForLargeRoom(room);
        void tightenLocalScreenShareForLargeRoom(room);
      } else {
        // Reaplica se câmera/tela publicaram depois da sala já ser "grande".
        void tightenLocalCameraForLargeRoom(room);
        void tightenLocalScreenShareForLargeRoom(room);
      }
    };

    const onTrackSubscribed = () => applyAdaptation();

    applyAdaptation();
    room.on(RoomEvent.ParticipantConnected, applyAdaptation);
    room.on(RoomEvent.ParticipantDisconnected, applyAdaptation);
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);

    return () => {
      room.off(RoomEvent.ParticipantConnected, applyAdaptation);
      room.off(RoomEvent.ParticipantDisconnected, applyAdaptation);
      room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
    };
  }, [room]);
}
